import { getErrorMessage } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import type BaseTreeView from '../../../../classes/pieces/BaseTreeView';
import useLiquidationTreeviewStore from '../../../../stores/liquidationTreeviewStore';

const { getState } = useLiquidationTreeviewStore;

/**
 * Relit dans l'arbre d'une année ce que la fiche a modifié et signalé au store (pièces, bordereaux), au retour
 * sur l'arbre ou si un signalement arrive pendant qu'il est affiché. En cas d'échec, les demandes sont remises
 * dans le store (isRafraichissementEnEchec) : pas de nouvel essai en boucle, mais au prochain affichage de l'arbre
 * ou à la prochaine demande.
 */
const useRelectureFiche = (
	anneeExercice: number,
	dataTreeView: BaseTreeView,
): void => {
	const aRafraichir = useLiquidationTreeviewStore(
		(state) => state.annees.get(anneeExercice)?.aRafraichir,
	);
	const isRacinesChargees = useLiquidationTreeviewStore(
		(state) => state.annees.get(anneeExercice)?.isRacinesChargees ?? false,
	);
	const isEnEchec = useLiquidationTreeviewStore(
		(state) =>
			state.annees.get(anneeExercice)?.isRafraichissementEnEchec ?? false,
	);

	// nouvel affichage de l'arbre : les demandes remises après un échec peuvent être relues
	React.useEffect(() => {
		getState().relancerRafraichissements(anneeExercice);
	}, [anneeExercice]);

	React.useEffect(() => {
		if (!isRacinesChargees || !aRafraichir || isEnEchec) return;
		const demandes = getState().prendreRafraichissements(anneeExercice);
		if (!demandes) return;

		dataTreeView
			.refreshRows(demandes)
			.then((appliquer) => getState().updateIndex(anneeExercice, appliquer))
			.catch((error) => {
				getState().rendreRafraichissements(anneeExercice, demandes);
				toast.error(
					<>
						{getErrorMessage(
							error,
							"Erreur lors de la mise à jour de l'arbre.",
						)}{' '}
						Elle sera relancée au prochain affichage de l'arbre.
					</>,
				);
			});
	}, [aRafraichir, isRacinesChargees, isEnEchec, anneeExercice, dataTreeView]);
};

export default useRelectureFiche;
